import { env, IS_DEV } from '@/lib/env'
import { TRACEPARENT_HEADER, newTraceparent } from '@/lib/traceparent'
import { ProblemSchema } from '@/lib/vision/schemas'
import {
  IMPLEMENTED_UPLOAD_CHANNELS,
  UploadResultSchema,
  type UploadChannel,
  type UploadResult,
} from './schemas'

/**
 * Pulse-side Flipkart upload. Posts to Vision with the Entra token; Vision
 * mints `aud=valkyrie` and forwards to Valkyrie. Do not POST to Valkyrie or
 * to Pulse `/api/...` (static S3 has no BFF).
 */

export type TokenGetter = () => Promise<string>
export type TenantGetter = () => string

export class ValkyrieApiError extends Error {
  readonly status: number
  readonly problem: unknown

  constructor(status: number, message: string, problem: unknown) {
    super(message)
    this.name = 'ValkyrieApiError'
    this.status = status
    this.problem = problem
  }
}

export interface ValkyrieUploadClientOptions {
  /** Vision origin. Defaults to `VITE_VISION_BASE_URL`. */
  baseUrl?: string
  getToken: TokenGetter
  getTenant?: TenantGetter
}

export function createValkyrieUploadClient(opts: ValkyrieUploadClientOptions) {
  const baseUrl = (opts.baseUrl ?? env.VITE_VISION_BASE_URL).replace(/\/$/, '')

  return {
    async uploadChannelPos(channel: string, files: File[]): Promise<UploadResult> {
      const normalized = channel.trim().toLowerCase() as UploadChannel
      if (!(IMPLEMENTED_UPLOAD_CHANNELS as readonly string[]).includes(normalized)) {
        throw new ValkyrieApiError(400, `${normalized} upload not implemented`, null)
      }

      const body = new FormData()
      for (const file of files) body.append('files', file)
      const tenant = opts.getTenant?.()
      if (tenant) body.append('tenantId', tenant)

      const token = await opts.getToken()
      const res = await fetch(`${baseUrl}/v1/channels/${encodeURIComponent(normalized)}/uploads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          [TRACEPARENT_HEADER]: newTraceparent(),
          ...(tenant ? { 'X-Tenant-Id': tenant } : {}),
        },
        body,
        credentials: 'omit',
      })

      if (!res.ok) throw await toApiError(res)

      const text = await res.text()
      const raw = text ? (JSON.parse(text) as unknown) : null
      const parsed = UploadResultSchema.safeParse(raw)
      if (!parsed.success) {
        if (IS_DEV) {
          console.warn('[valkyrie] upload response failed schema', parsed.error.flatten(), raw)
        }
        throw new ValkyrieApiError(200, 'Malformed upload response', parsed.error.flatten())
      }
      return parsed.data
    },
  }
}

export type ValkyrieUploadClient = ReturnType<typeof createValkyrieUploadClient>

async function toApiError(res: Response): Promise<ValkyrieApiError> {
  const text = await res.text().catch(() => '')
  if (!text) {
    return new ValkyrieApiError(res.status, `${res.status} ${res.statusText}`, null)
  }
  try {
    const raw = JSON.parse(text) as unknown
    const problem = ProblemSchema.safeParse(raw)
    if (problem.success) {
      return new ValkyrieApiError(res.status, problem.data.detail || problem.data.title, problem.data)
    }
    return new ValkyrieApiError(res.status, `${res.status} ${res.statusText}`, raw)
  } catch {
    return new ValkyrieApiError(res.status, text, null)
  }
}
